import { LegalLinksAndVersion } from '@Components/Legal/LegalLinksAndVersion';
import { LoginButton } from '@Components/LoginButton';
import { useCustomDomainUserInformation } from '@Contexts/customDomain/useCustomDomainUserInformation';
import { useIsAuthenticated } from '@azure/msal-react';
import { useSession } from '@Contexts/session/SessionContext';
import { type ApiUserPartnershipInformation, EnumAccessState, EnumPartnershipConsent } from '@Model';
import { FlexColumn, Modal } from '@synqo/frontend-component-library';
import { BrandLogo } from '@Components/Navigation/BrandLogo';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { type ClientInfo, ClientSelection } from './ClientSelection';
import { MandantSelection } from './MandantSelection';
import { NoAccess } from './NoAccess';

interface LoginPageProps {
    redirectAfterSelectionPath?: string;
}

export const LoginPage: FC<LoginPageProps> = ({ redirectAfterSelectionPath }) => {
    const { isLoading, isError, data: userInformation } = useCustomDomainUserInformation();
    const isAuthenticated = useIsAuthenticated();
    const { update } = useSession();

    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedPartnership, setSelectedPartnership] = useState<ApiUserPartnershipInformation | undefined>();

    const partnerships = useMemo<ApiUserPartnershipInformation[]>(
        () =>
            (userInformation?.partnerships ?? []).filter(
                (p) => p.partnership_consent !== EnumPartnershipConsent.Rejected,
            ),
        [userInformation?.partnerships],
    );

    const accessiblePartnerships = useMemo<ApiUserPartnershipInformation[]>(
        () => partnerships.filter((p) => p.access_state !== EnumAccessState.Locked),
        [partnerships],
    );

    const availableClients = useMemo<ClientInfo[]>(
        () =>
            Array.from(
                new Map(
                    partnerships.map((partnership) => [
                        partnership.client_id,
                        {
                            id: partnership.client_id,
                            display: partnership.client_display,
                            locked: partnership.access_state === EnumAccessState.Locked,
                        },
                    ]),
                ).values(),
            ),
        [partnerships],
    );

    const accessibleClients = useMemo<ClientInfo[]>(
        () => availableClients.filter((c) => !c.locked),
        [availableClients],
    );

    const partnershipsWithClient = useMemo(
        () => partnerships.filter((p) => p.client_id === selectedClientId),
        [partnerships, selectedClientId],
    );

    const accessiblePartnershipsForClient = useMemo(
        () => partnershipsWithClient.filter((p) => p.access_state !== EnumAccessState.Locked),
        [partnershipsWithClient],
    );

    const handleSelectPartnership = useCallback(
        (partnership: ApiUserPartnershipInformation) => {
            update({
                partnershipId: partnership.partnership_id,
                partnershipType: partnership.type,
                clientId: partnership.client_id,
                subpath: partnership.mandant_shorthand,
                mandantId: partnership.mandant_id,
            });
            setSelectedPartnership(partnership);
        },
        [update, setSelectedPartnership],
    );

    useEffect(() => {
        if (accessibleClients.length === 1) {
            setSelectedClientId(accessibleClients[0].id);
        }
    }, [accessibleClients, selectedClientId, setSelectedClientId]);

    useEffect(() => {
        if (accessiblePartnershipsForClient.length === 1 && selectedPartnership === undefined) {
            handleSelectPartnership(accessiblePartnershipsForClient[0]);
        }
    }, [accessiblePartnershipsForClient, selectedPartnership, handleSelectPartnership]);

    const modalContent = useMemo(() => {
        if (isLoading) {
            return 'Wird geladen...';
        }

        if (!isAuthenticated) {
            return <LoginButton />;
        }

        if (isError) {
            return (
                <NoAccess message="Ihre Zugänge konnten nicht geladen werden. Bitte versuchen Sie es später erneut." />
            );
        }

        if (accessiblePartnerships.length === 0) {
            return <NoAccess message="Es ist aktuell kein Mandant zur Auswahl verfügbar." />;
        }

        if (selectedPartnership !== undefined) {
            const target = redirectAfterSelectionPath
                ? `/${selectedPartnership.mandant_shorthand}${redirectAfterSelectionPath}`
                : '/';
            return <Navigate to={target} />;
        }

        const selectedClient =
            accessibleClients.length === 1
                ? accessibleClients[0]
                : availableClients.find((c) => c.id === selectedClientId);

        if (selectedClient !== undefined) {
            return (
                <MandantSelection
                    client={selectedClient.display}
                    partnerships={partnershipsWithClient}
                    hasOnlyOneClient={accessibleClients.length === 1}
                    selectPartnership={handleSelectPartnership}
                    backToClientSelection={() => setSelectedClientId('')}
                />
            );
        }

        return <ClientSelection clients={availableClients} selectClient={setSelectedClientId} />;
    }, [
        isLoading,
        isAuthenticated,
        isError,
        accessiblePartnerships,
        selectedPartnership,
        selectedClientId,
        availableClients,
        accessibleClients,
        partnershipsWithClient,
        setSelectedClientId,
        handleSelectPartnership,
        redirectAfterSelectionPath,
    ]);

    return (
        <Modal isOpen onClose={() => false} withoutCloseButton>
            <FlexColumn>
                <BrandLogo width="100%" className="max-h-30" />
                {modalContent}
                <LegalLinksAndVersion />
            </FlexColumn>
        </Modal>
    );
};
